import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import { theme } from "../../../theme";
import Body from "../../atoms/typography/Body";
import BodySmall from "../../atoms/typography/BodySmall";
import Checkbox from "../../atoms/inputs/Checkbox";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import TableRow from "../../atoms/TableRow";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import {
  PERMISSION_CATEGORIES,
  type PermissionOverridePreview,
} from "../../../models/permissionLabels";

interface Props {
  effectivePermissions: PermissionOverridePreview;
  editable: boolean;
}

const GroupSectionRow = styled(Row)<{ $isLast?: boolean }>`
  background-color: ${theme.colors.grey02};
  padding: 8px;
  border-top: 2px solid ${theme.colors.grey05};
  ${({ $isLast }) =>
    $isLast && `border-bottom: 2px solid ${theme.colors.grey05};`}
`;

const UserPermissionsList: FC<Props> = ({ effectivePermissions, editable }) => {
  const { t } = useTranslation();
  const [openCategories, setOpenCategories] = useState<Array<string>>([]);

  const toggleCategoryOpen = (key: string) => {
    setOpenCategories((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  return (
    <div>
      {PERMISSION_CATEGORIES.map(({ key, titleKey, labels }, index) => {
        const override = effectivePermissions[key];
        const isOpen = openCategories.includes(key);
        const isLast = index === PERMISSION_CATEGORIES.length - 1;

        return (
          <div key={key}>
            <GroupSectionRow
              justify="between"
              align="center"
              nogutter
              onClick={() => toggleCategoryOpen(key)}
              $isLast={isLast}
            >
              <Body fontweight="500">{t(titleKey)}</Body>
              <div onClick={(e) => e.stopPropagation()}>
                <ChevronButton
                  type={isOpen ? "up" : "down"}
                  onClick={() => toggleCategoryOpen(key)}
                />
              </div>
            </GroupSectionRow>
            <SlideOutHeight isVisible={isOpen}>
              {Object.entries(labels).map(([code, labelKey]) => (
                <TableRow key={code} clickable={false}>
                  <Col lg={4}>
                    <BodySmall>{t(labelKey)}</BodySmall>
                  </Col>
                  <Col
                    lg={2}
                    style={{ display: "flex", gap: 4, alignItems: "center" }}
                  >
                    <Checkbox
                      title={t("random.read")}
                      checked={override?.read?.includes(code) ?? false}
                      onChange={() => {}}
                      disabled={!editable}
                    />
                  </Col>
                  <Col
                    lg={2}
                    style={{ display: "flex", gap: 4, alignItems: "center" }}
                  >
                    <Checkbox
                      title={t("random.write")}
                      checked={override?.write?.includes(code) ?? false}
                      onChange={() => {}}
                      disabled={!editable}
                    />
                  </Col>
                </TableRow>
              ))}
            </SlideOutHeight>
          </div>
        );
      })}
    </div>
  );
};

export default UserPermissionsList;
